<template>
  <div :class="{ 'q-mt-md': !isMobile }">
    <q-card
      v-if="post"
      :class="[
        'post-card',
        {
          'border-radius-20': !isMobile && !isColumnLayout,
          'q-pt-xs q-pb-lg': isInternalPage,
          'border-radius-0 column-layout': isColumnLayout,
        },
      ]"
      flat
    >
      <q-card-section
        v-if="isColumnLayout"
        :class="['q-pb-none', isMobile ? 'q-my-lg' : 'q-my-xl']"
      >
        <h1 :class="['fs-40 fw-700 lh-51', isMobile ? 'q-px-sm q-pt-md' : 'q-px-lg']">
          {{ post.title }}
        </h1>
        <div :class="['post-date fs-12', isMobile ? 'q-px-sm' : 'q-px-lg']">
          {{ dayjs.unix(post.createdAt.timestamp).format('DD.MM.YYYY') }}
          {{ dayjs.unix(post.createdAt.timestamp).format('HH:mm') }}
        </div>
      </q-card-section>

      <div :class="['relative-position', { 'column-image': isColumnLayout && !isMobile }]">
        <img
          v-if="coverSrc"
          ref="coverImageRef"
          :src="coverSrc"
          :alt="post.metaFields.coverImageAlt"
          width="100%"
          height="auto"
        >
        <Watermark
          v-if="showWatermark && coverSrc"
          :class="[
            'image-watermark',
            { 'mobile-watermark': isMobile, 'column-watermark': isColumnLayout },
          ]"
          :small="isMobile"
        />
      </div>

      <Watermark
        v-if="showWatermark"
        class="post-watermark"
        :small="isMobile"
      />

      <q-card-section
        v-if="!isColumnLayout"
        :class="['q-pb-none', isMobile ? 'q-mt-lg' : 'q-mt-xl']"
      >
        <div :class="['post-date fs-12', isMobile ? 'q-px-sm' : 'q-px-xl']">
          {{ dayjs.unix(post.createdAt.timestamp).format('DD.MM.YYYY') }}
          {{ dayjs.unix(post.createdAt.timestamp).format('HH:mm') }}
        </div>
        <h1 :class="['fs-40 fw-700 lh-51', isMobile ? 'q-px-sm' : 'q-px-xl']">
          {{ post.title }}
        </h1>
      </q-card-section>

      <template v-if="!isMobile">
        <q-separator
          v-if="isColumnLayout"
          class="break-line"
        />

        <ul
          v-if="anchors?.length"
          :class="['page-anchors flex column gap-md', { 'column-anchors': isColumnLayout }]"
        >
          {{ $t('text.tableContent') }}
          <li
            v-for="anchor in anchors"
            :key="anchor.title"
            class="anchor-item q-pl-lg cursor-pointer"
            @click="scrollTo(anchor.element)"
          >
            {{ anchor.title }}
          </li>
        </ul>
      </template>

      <q-card-section>
        <div
          ref="contentRef"
          :class="['post-content', isMobile ? 'q-px-sm' : 'q-px-xl post-content-desktop']"
          v-html="post.content"
        />
      </q-card-section>
      <div
        v-if="!isInternalPage"
        :class="[
          'post-card-footer q-pa-lg',
          { 'flex items-centers justify-between': !isMobile, 'column-footer': isColumnLayout && !isMobile },
        ]"
      >
        <PostAuthors
          v-if="post.authors?.length"
          :authors="post.authors"
        />
        <div
          v-else-if="post.metaFields?.metaAuthor"
          class="flex items-center"
        >
          <AccountAvatar
            class="q-mr-sm"
            :url="post.author?.photoUrl"
            size="40px"
          />
          <div class="post-author-name flex items-center fs-15 q-pl-xs">
            {{ post.metaFields?.metaAuthor }}
          </div>
        </div>
        <div v-else />
        <div :class="[
          'flex items-center',
          { 'justify-center': !$q.platform.is.mobile },
          { 'share-block-mobile q-mt-lg q-pt-lg': $q.platform.is.mobile },
        ]">
          <span
            v-t="'label.share'"
            class="q-mr-md share-label"
          />
          <ShareNetwork
            v-for="(network, index) in networks"
            :key="network.network"
            ref="shareNetworkRef"
            class="share-network cursor-pointer q-pa-sm"
            :network="network.network"
            :url="post.publicUrl"
            :title="post.title"
            :media="post.coverImageGuid?.url"
            @open="onOpenShareModal(index)"
          >
            <q-icon
              :name="`img:${network.icon}`"
              size="20px"
            />
          </ShareNetwork>
        </div>
      </div>
    </q-card>
    <AppLoader v-else />

    <div :class="{ 'column-controls-wrapper': isColumnLayout && !isMobile }">

      <Newsletter
        v-if="settings.isNewsletterEnabled"
        :meta-fields="settings.metaFields"
        :is-horizontal="!isMobile"
        :class="{ 'newsletter-form-mobile': isMobile, 'is-column': isColumnLayout }"
        :is-column-layout="isColumnLayout"
        @subscribe="$emit('subscribe', $event)"
      />

      <q-carousel
        v-if="slides.length > 0"
        ref="carouselRef"
        v-model="slide"
        transition-prev="slide-right"
        transition-next="slide-left"
        swipeable
        animated
        height="530px"
        :class="['carousel q-pt-xl q-mt-lg']"
      >
        <q-carousel-slide
          v-for="(slideItems, index) in slides"
          :key="index"
          :name="index"
          :class="[
            'column no-wrap q-px-none q-py-lg',
            { 'q-px-none': isMobile && isColumnLayout },
            { 'q-px-lg': isMobile && !isColumnLayout },
          ]"
        >
          <div class="row fit justify-start items-center no-wrap gap-lg">
            <div
              v-for="item in slideItems"
              :key="item.guid"
              :class="[
                'slide-post-card col full-height',
                { 'slide-post-card-mobile': isMobile, 'slide-post-card-column': !isMobile && isColumnLayout },
              ]"
            >
              <BlogBanner
                v-if="item.isBanner"
                :banner="settings.metaFields.banner"
              />
              <BasicListItem
                v-else
                :post="item"
                container-class="q-pa-none full-height"
                :is-column-layout="isColumnLayout"
              />
            </div>
          </div>
        </q-carousel-slide>

        <template v-slot:control>
          <q-carousel-control
            position="top-right"
            :offset="[0, 0]"
            :class="[
              'full-width flex justify-between items-center',
              { 'q-px-none': isMobile && isColumnLayout },
              { 'q-px-lg': isMobile && !isColumnLayout },
            ]"
          >
            <div
              v-t="'label.seeAlso'"
              class="carousel-label fs-30"
            />
            <div class="flex gap-sm">
              <q-btn
                :class="[
                  'carousel-button fs-12',
                  isColumnLayout ? 'border-radius-0' : 'border-radius-10',
                ]"
                icon="img:arrow-left.svg"
                aria-label="<-"
                flat
                @click="$refs.carouselRef.previous()"
              />
              <q-btn
                :class="[
                  'carousel-button',
                  isColumnLayout ? 'border-radius-0' : 'border-radius-10',
                ]"
                icon="img:arrow-right.svg"
                aria-label="->"
                flat
                @click="$refs.carouselRef.next()"
              />
            </div>
          </q-carousel-control>
        </template>
      </q-carousel>

      <div :class="{
        'q-px-none': isMobile && isColumnLayout,
        'q-px-lg': isMobile && !isColumnLayout,
        'q-pb-xl': isColumnLayout,
      }">
        <AppBtn
          v-if="posts.length > 1"
          :class="[
            'button full-width text-weight-bold',
            isColumnLayout ? 'border-radius-0' : 'border-radius-10',
          ]"
          padding="23px"
          :label="$t('label.allArticles')"
          @click="router.push({ name: ROUTES.RENDER.BLOG })"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import {
 computed, onBeforeUnmount, onMounted, ref, watch,
} from 'vue';
import dayjs from 'dayjs';
import AppLoader from 'components/AppLoader.vue';
import AccountAvatar from 'components/AccountAvatar.vue';
import { useRenderStore } from 'stores/render';
import BasicListItem from 'components/render/BasicListItem.vue';
import AppBtn from 'components/AppBtn.vue';
import { useRoute, useRouter } from 'vue-router';
import { ROUTES } from 'src/enums';
import { storeToRefs } from 'pinia';
import BlogBanner from 'components/BlogBanner.vue';
import { useMeta, useQuasar } from 'quasar';
import getThumb from 'src/helpers/getThumb';
import useSchemaOrgPostData from 'src/composables/useSchemaOrgPostData';
import Newsletter from 'components/Newsletter.vue';
import Watermark from 'components/Watermark.vue';
import PostAuthors from 'components/PostAuthors.vue';
import { initCookies } from 'boot/axios';
import useRender from 'src/composables/useRender';

const router = useRouter();
const route = useRoute();

defineOptions({
  // eslint-disable-next-line consistent-return
  async preFetch({
    store, currentRoute, ssrContext, redirect,
  }) {
    const renderStore = useRenderStore(store);
    const host = ssrContext
      ? ssrContext.req.hostname
      : window.location.hostname;
    try {
      // eslint-disable-next-line prefer-const
      let { funkitoolsPreview, accessToken } = currentRoute.query;
      const query = funkitoolsPreview ? '?funkitoolsPreview=1' : '';

      const cookies = initCookies(ssrContext);

      if (!accessToken) {
        accessToken = cookies.get('accessToken');
      } else {
        cookies.set('accessToken', accessToken);
      }

      if (!renderStore.post) {
        const headers = accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
        await renderStore.getPost({
          slug: `${currentRoute.params.postSlug}${query}`,
          host,
          headers,
        });
      }
    } catch (error) {
      const { status } = error.response;
      if (status === 400) {
        redirect({ name: ROUTES.RENDER.NOT_FOUND });
      }
    }
  },
});

defineEmits(['subscribe']);

const renderStore = useRenderStore();
const $q = useQuasar();

const {
  post,
  posts,
  settings,
  anchors,
} = storeToRefs(renderStore);
const { scrollTo } = useRender();

let observer;

const contentRef = ref(null);
const carouselRef = ref(null);
const coverImageRef = ref(null);
const shareNetworkRef = ref(null);
const slide = ref(0);

const isMobile = computed(() => ($q.platform.is.mobile || $q.screen.width <= 1024));
const isInternalPage = computed(() => post.value.entryKey === 'blogInternalPage');
const isColumnLayout = computed(() => settings.value?.metaFields?.templateLayout === 'column');

const slides = computed(() => {
  let chunkSize = 2;
  if (isColumnLayout.value) {
    chunkSize = 3;
  }
  if (isMobile.value) {
    chunkSize = 1;
  }
  const array = posts.value.filter((item) => item.guid !== post.value.guid);
  return Array.from(
    { length: Math.ceil(array.length / chunkSize) },
    (_, i) => array.slice(i * chunkSize, i * chunkSize + chunkSize),
  );
});

const coverSrc = computed(() => (post.value.coverImageGuid ? getThumb(post.value.coverImageGuid.url) : null));
const showWatermark = computed(() => !settings.value.metaFields?.removeBranding);

function updateAnchors() {
  const h2NodeList = contentRef.value?.querySelectorAll('h2') || [];
  const anchorsList = [...h2NodeList]
    .filter((element) => element.textContent?.length)
    .map((element) => ({
      title: element.textContent,
      element,
    }));
  renderStore.anchors = anchorsList;
}

const schemaOrgData = useSchemaOrgPostData();

const networks = [
  {
    network: 'facebook',
    label: 'Facebook',
    icon: '/social/facebook.svg',
  },
  {
    network: 'telegram',
    label: 'Telegram',
    icon: '/social/telegram.svg',
  },
  {
    network: 'twitter',
    label: 'Twitter',
    icon: '/social/x.svg',
  },
  {
    network: 'linkedin',
    label: 'LinkedIn',
    icon: '/social/linkedin.svg',
  },
  {
    network: 'pinterest',
    label: 'Pinterest',
    icon: '/social/pinterest.svg',
  },
];

function onOpenShareModal(index) {
  const shareNetworkItemRef = shareNetworkRef.value[index];
  if (shareNetworkItemRef === null) return;

  clearInterval(shareNetworkItemRef.popupInterval);
  shareNetworkItemRef.popupWindow = undefined;
}

useMeta(() => ({
  title: post.value.title,
  meta: {
    description: {
      name: 'description',
      content: post.value.metaFields.metaDescription || '',
    },
    author: {
      name: 'author',
      content: post.value.metaFields.metaAuthor || '',
    },
    keywords: {
      name: 'keywords',
      content: post.value.metaFields.metaKeywords || '',
    },
    ogSiteName: {
      property: 'og:site_name',
      content: settings.value.metaFields.metaTitle,
    },
    ogTitle: {
      property: 'og:title',
      content: post.value.title?.replaceAll('"', ''),
    },
    ogDescription: {
      property: 'og:description',
      content: post.value.metaFields.description,
    },
    ogUrl: {
      property: 'og:url',
      content: post.value.publicUrl,
    },
    ogType: {
      property: 'og:type',
      content: 'article',
    },
    articlePublisher: {
      property: 'article:publisher',
      content: settings.value.publicHost,
    },
    ogImage: {
      property: 'og:image',
      content: post.value.coverImageGuid?.url,
    },
    ogImageWidth: {
      property: 'og:image:width',
      content: coverImageRef.value?.width,
    },
    ogImageHeight: {
      property: 'og:image:height',
      content: coverImageRef.value?.width,
    },
    twitterCard: {
      property: 'twitter:card',
      content: 'summary_large_image',
    },
    twitterImage: {
      property: 'twitter:image',
      content: post.value.coverImageGuid?.url,
    },
    indexing: {
      name: 'robots',
      content: post.value.metaFields.noindex ? 'noindex' : 'index',
    },
  },
  link: {
    material: { rel: 'image_src', href: post.value.coverImageGuid?.url },
  },
  script: {
    ldJson: {
      type: 'application/ld+json',
      innerHTML: JSON.stringify(schemaOrgData.value),
    },
  },
}));

watch(contentRef, () => {
  updateAnchors();
  observer = new MutationObserver(() => {
    updateAnchors();
  });
  observer.observe(contentRef.value, {
    subtree: true,
    childList: true,
    attributes: true,
  });
});

onBeforeUnmount(() => {
  observer.disconnect();
  renderStore.post = null;
});

onMounted(() => {
  router.replace({
    query: {
      ...route.query,
      accessToken: undefined,
    },
  });
});
</script>
<style lang="scss" scoped>
.post-card {
  background: var(--blog-card-color, #fff);
  overflow: hidden;
}
.post-date,
.post-author-label,
.share-label {
  opacity: 0.5;
}
.post-date {
  line-height: 12px;
}
.post-card-footer {
  border-top: 1px solid var(--blog-border-color, $grey-4);
}
.post-content {
  :deep(img) {
    max-width: 100%;
    height: auto;
    border-radius: 20px;
  }

  :deep(.iframe-wrapper) {
    border-radius: 20px;
    position: relative;
    padding-bottom: 56.25%;
    overflow: hidden;
    width: 100%;
    height: auto;

    iframe {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
    }
  }
}
.carousel {
  background: var(--blog-background-color, $grey-1);
}
.carousel-label {
  color: var(--blog-font-color, $grey-4);
}
.carousel-button {
  width: 44px;
  height: 44px;
  background: var(--blog-card-color, #fff);
  color: var(--blog-font-color, $grey-4);
  opacity: 0.5;

  &:hover {
    color: var(--blog-primary-color, $primary);
    opacity: 1;
  }
}
.slide-post-card {
  max-width: 49.9%;
}
.slide-post-card-column {
  max-width: 33.3%;
}
.slide-post-card-mobile {
  max-width: 100%;
}
.button {
  background: var(--blog-card-color, #fff);
  color: var(--blog-font-color, $grey-4);

  &:hover {
    color: var(--blog-primary-color, $primary);
  }
}
.post-author-label {
  line-height: 12px;
}
.post-author-name {
  line-height: 15px;
}

.column-controls-wrapper {
  padding-left: 40px;
  padding-right: 40px;
}

.newsletter-form-mobile {
  max-width: unset !important;
  margin: 16px 24px !important;

  &.is-column {
    margin: 16px 0 !important;
  }
}

.break-line {
  background: var(--blog-border-color, $grey-4);
  margin: 48px 40px;
}

ul.page-anchors {
  list-style-type: none;
  padding: 24px 0 8px;
  margin: 0 64px;

  &.column-anchors {
    border-bottom: 1px solid var(--blog-border-color, $grey-4);
    margin: 0 80px;
    padding: 4px 0 24px;
  }
}

.column-image {
  padding: 0 40px;
}

.column-layout {
  .post-content-desktop {
    padding: 0 68px;
  }

  .column-footer {
    margin: 0 80px;
  }
}

.share-label {
  opacity: 0.5;
}
.share-block-mobile {
  border-top: 1px solid var(--blog-border-color, $grey-4);
}

.image-watermark {
  position: absolute;
  left: 24px;
  top: 24px;

  &.column-watermark {
    top: 16px;
    left: 66px;
  }

  &.mobile-watermark {
    top: 16px;
    left: 16px;
  }
}

.post-watermark {
  margin: 0 auto;
  margin-top: 16px;
}
</style>
